import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import { useInvoice, useUpdateInvoice, useCustomers } from "@/hooks/use-api";
import {
  invoiceFields,
  invoiceToFormValues,
  type InvoiceFormValues,
} from "./invoice-form-config";

export default function V2InvoiceEditPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const invoiceQuery = useInvoice(id);
  const updateInvoice = useUpdateInvoice();
  const customersQuery = useCustomers({ limit: 1000 });
  const [error, setError] = useState<string | null>(null);

  const customerOptions = (
    (
      customersQuery.data as
        | { customers?: Record<string, unknown>[] }
        | undefined
    )?.customers ?? []
  ).map((c) => ({
    value: String(c.contact_role_id ?? ""),
    label:
      [c.first_name, c.last_name].filter(Boolean).join(" ") ||
      String(c.company_name ?? ""),
  }));

  async function onSubmit(values: InvoiceFormValues) {
    setError(null);
    try {
      await updateInvoice.mutateAsync({
        id,
        input: values as Record<string, unknown>,
      });
      toast.success("Invoice updated");
      navigate(`/invoices/${id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your changes — check the fields and try again.",
      );
    }
  }

  const invoice = (
    invoiceQuery.data as { invoice?: Record<string, unknown> } | undefined
  )?.invoice;

  // The customer select is required — gate on the lookup's loading/error
  // states instead of silently rendering an empty select.
  if (invoiceQuery.isLoading || customersQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (customersQuery.isError) {
    return (
      <EmptyState
        title="Could not load customers"
        description="The customer list failed to load, so the invoice form can't be edited yet."
        action={{ label: "Try again", onClick: () => customersQuery.refetch() }}
      />
    );
  }

  if (invoiceQuery.isError || !invoice) {
    return (
      <EmptyState
        title="Invoice not found"
        description="This invoice may have been removed, or you don't have access to it."
        action={{
          label: "Back to invoices",
          onClick: () => navigate("/invoices"),
        }}
      />
    );
  }

  return (
    <EntityForm<InvoiceFormValues>
      title="Edit invoice"
      breadcrumbs={[
        { label: "Invoices", href: "/invoices" },
        { label: "Edit" },
      ]}
      defaultValues={invoiceToFormValues(invoice)}
      fields={invoiceFields(customerOptions)}
      submitLabel="Save changes"
      onSubmit={onSubmit}
      cancelHref={`/invoices/${id}`}
      error={error}
      isSubmitting={updateInvoice.isPending}
    />
  );
}
